'use client';

import { useState } from 'react';

import Link from 'next/link';

import type { ServicesBlock } from '~/(tenant)/_lib/schema/landing-config.schema';

import { type LandingRenderContext, landingImageUrl, pickLocalized } from './landing-context';
import styles from './services-explorer.module.css';

export function ServicesExplorer({ block, ctx }: { block: ServicesBlock; ctx: LandingRenderContext }) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const arabic = ctx.locale.startsWith('ar');
  const cards = block.mode === 'manual' ? block.cards ?? [] : ctx.services.map((service) => ({
    title: service.name,
    description: service.description,
    href: (block.autoLinkToBooking ?? true) ? '/book' : undefined,
  }));
  const categories = block.mode === 'manual' ? (block.categories ?? []).filter((category) => pickLocalized(category.label, category.label_ar, ctx.locale)?.trim()) : [];
  const activeCategory = categories.some((category) => category.id === selectedCategory) ? selectedCategory : null;
  const title = pickLocalized(block.title, block.title_ar, ctx.locale);
  const subtitle = pickLocalized(block.subtitle, block.subtitle_ar, ctx.locale);
  const eyebrow = pickLocalized(block.eyebrow, block.eyebrow_ar, ctx.locale);
  const visibleCards = filterExplorerCards(cards, activeCategory);

  return (
    <section className={styles.section} data-test="services-explorer" dir={arabic ? 'rtl' : undefined}>
      <div className={styles.inner}>
        <div className={styles.heading}>
          {eyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
          {title ? <h2>{title}</h2> : null}
          {subtitle ? <p className="landing-prose">{subtitle}</p> : null}
        </div>
        {categories.length ? (
          <div className={styles.filters} role="group" aria-label={arabic ? 'تصفية الأنشطة' : 'Filter activities'}>
            {[{ id: null, label: arabic ? 'الكل' : 'All', label_ar: undefined }, ...categories].map((category) => (
              <button key={category.id ?? '__all'} type="button" data-sports-trigger aria-pressed={activeCategory === category.id} onClick={() => setSelectedCategory(category.id)}>
                {pickLocalized(category.label, category.label_ar, ctx.locale)}
              </button>
            ))}
          </div>
        ) : null}
        <div className={styles.cards} data-columns={block.columns ?? 3} key={activeCategory ?? '__all'}>
          {visibleCards.map((card, index) => {
            const cardTitle = pickLocalized(card.title, card.title_ar, ctx.locale);
            const description = pickLocalized(card.description, card.description_ar, ctx.locale);
            const content = (
              <>
                {card.imageKey ? (
                  <div className={styles.media}>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={landingImageUrl(ctx, card.imageKey)} alt={cardTitle ?? ''} loading="lazy" />
                  </div>
                ) : null}
                <h3>{cardTitle}</h3>
                {description ? <p className="landing-prose">{description}</p> : null}
                {card.href ? <span className={styles.cta}>{pickLocalized(card.ctaLabel, card.ctaLabel_ar, ctx.locale) || (arabic ? 'اعرف المزيد' : 'Learn more')}<span aria-hidden="true">{arabic ? '←' : '→'}</span></span> : null}
              </>
            );
            if (!card.href) return <article key={index} className={styles.card}>{content}</article>;
            return /^https?:\/\//i.test(card.href) ? (
              <a key={index} href={card.href} className={styles.card} target="_blank" rel="noopener noreferrer">{content}</a>
            ) : <Link key={index} href={card.href} className={styles.card}>{content}</Link>;
          })}
        </div>
        {!visibleCards.length ? <p className={styles.empty} role="status">{arabic ? 'لا توجد أنشطة في هذه الفئة.' : 'No activities in this category.'}</p> : null}
      </div>
    </section>
  );
}

export function filterExplorerCards(cards: NonNullable<ServicesBlock['cards']>, categoryId: string | null) {
  return categoryId ? cards.filter((card) => card.categoryIds?.includes(categoryId)) : cards;
}
